import React from 'react';
import { Switch, Route, Link, useLocation, useRouteMatch } from 'react-router-dom';
import UsersTable from './UsersTable';
import Maintenance from './Maintenance';
import MonitoredAccounts from './MonitoredAccounts';
import AiSettings from './AiSettings';
import AiLogs from './AiLogs';
import Features from './Features';
import TokenConsumption from './TokenConsumption';
import PriceSettings from './PriceSettings';
import SupportTickets from './SupportTickets';
import TrendFetchLive from './TrendFetchLive';
import { cn } from "../../lib/utils";
import { Users, Wrench, ShieldCheck, ChevronRight, Share2, Sparkles, BrainCircuit, Coins, DollarSign, MessageSquare, TrendingUp } from 'lucide-react';

export default function AdminPage() {
  const { path } = useRouteMatch();
  const location = useLocation();

  const navItems = [
    { label: 'Users', path: `${path}/users`, icon: Users },
    { label: 'Monitored Accounts', path: `${path}/monitored-accounts`, icon: Share2 },
    { label: 'Features', path: `${path}/features`, icon: ShieldCheck },
    { label: 'AI Settings', path: `${path}/ai-settings`, icon: Sparkles },
    { label: 'AI Logs', path: `${path}/ai-logs`, icon: BrainCircuit },
    { label: 'Consumption', path: `${path}/consumption`, icon: Coins },
    { label: 'Price Settings', path: `${path}/prices`, icon: DollarSign },
    { label: 'Support Tickets', path: `${path}/support`, icon: MessageSquare },
    { label: 'Trend Fetch', path: `${path}/trend-fetch`, icon: TrendingUp },
    { label: 'Maintenance', path: `${path}/maintenance`, icon: Wrench },
  ];

  return (
    <div className="container mx-auto py-10 px-4 max-w-7xl">
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar Navigation */}
        <aside className="lg:w-64 shrink-0 space-y-8">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-primary font-black uppercase tracking-[0.2em] text-[10px]">
              <ShieldCheck className="h-3 w-3" />
              Administration
            </div>
            <h1 className="text-3xl font-black text-foreground tracking-tight">System Control</h1>
          </div>

          <nav className="flex flex-col bg-card p-2 rounded-2xl border border-border shadow-xl">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all group",
                  location.pathname === item.path
                    ? "bg-primary text-white shadow-lg shadow-primary/20"
                    : "text-muted-foreground hover:text-foreground hover:bg-card/50"
                )}
              >
                <div className={cn(
                  "p-1.5 rounded-lg transition-colors",
                  location.pathname === item.path
                    ? "bg-primary/50 text-white"
                    : "bg-muted group-hover:bg-muted text-muted-foreground group-hover:text-foreground"
                )}>
                  <item.icon className="h-4 w-4" />
                </div>
                {item.label}
                <ChevronRight className={cn(
                  "ml-auto h-4 w-4 transition-all",
                  location.pathname === item.path ? "opacity-100" : "opacity-0 -translate-x-2"
                )} />
              </Link>
            ))}
          </nav>

          <div className="p-6 rounded-2xl border border-border bg-muted/30 space-y-3">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">System Status</h4>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs font-bold text-foreground">All services online</span>
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 min-w-0">
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <Switch>
              <Route exact path={`${path}/users`} component={UsersTable} />
              <Route exact path={`${path}/monitored-accounts`} component={MonitoredAccounts} />
              <Route exact path={`${path}/features`} component={Features} />
              <Route exact path={`${path}/ai-settings`} component={AiSettings} />
              <Route exact path={`${path}/ai-logs`} component={AiLogs} />
              <Route exact path={`${path}/consumption`} component={TokenConsumption} />
              <Route exact path={`${path}/prices`} component={PriceSettings} />
              <Route exact path={`${path}/support`} component={SupportTickets} />
              <Route exact path={`${path}/trend-fetch`} component={TrendFetchLive} />
              <Route exact path={`${path}/maintenance`} component={Maintenance} />
              <Route path={path}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="group bg-card p-8 rounded-2xl border border-border hover:border-primary/50 hover:bg-primary/5 transition-all space-y-4 shadow-xl"
                    >
                      <div className="h-12 w-12 rounded-xl bg-muted flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors shadow-inner">
                        <item.icon className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-foreground group-hover:text-primary flex items-center gap-2">
                          {item.label}
                          <ChevronRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                        </h3>
                        <p className="text-muted-foreground text-sm mt-1 leading-relaxed">
                          {item.label === 'Users'
                            ? 'Manage platform users, check statuses and roles.'
                            : item.label === 'Monitored Accounts'
                              ? 'Manage influencers and accounts monitored for trends.'
                              : item.label === 'Features'
                                ? 'Enable/disable features globally and manage tier-based gating.'
                                : item.label === 'AI Settings'
                                  ? 'Configure OpenAI models used for different system features.'
                                  : item.label === 'AI Logs'
                                    ? 'Monitor AI decisions, prompts and model responses.'
                                    : item.label === 'Consumption'
                                      ? 'Track token and API credit usage across all services.'
                                      : item.label === 'Price Settings'
                                        ? 'Configure cost per million tokens and external API credits.'
                                        : item.label === 'Support Tickets'
                                          ? 'Manage user support requests and send email replies.'
                                          : item.label === 'Trend Fetch'
                                            ? 'Live debug view of the trend detection pipeline.'
                                            : 'Cleanup trends, refresh analytics and system maintenance.'}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </Route>
            </Switch>
          </div>
        </main>
      </div>
    </div>
  );
}

